import React, {useContext, useState} from "react";
import {Card, CardActions, CardContent, Snackbar, TextField, Typography} from "@mui/material";
import Grid from "@mui/material/Grid";
import LoadingButton from '@mui/lab/LoadingButton';
import {LanguageContext} from "../context/LanguageContext";
import ReactRecaptcha3 from 'react-google-recaptcha3';
import axios from "axios";
import {env} from "../properties";
import {Alert} from "@mui/material";

const snackbarInitialState = {
  open: false,
  message: "",
  severity: "success"
};

const useValidator = true;

ReactRecaptcha3.init(env.reCaptcha);

const Registration = () => {
  const langContext = useContext(LanguageContext);

  const [loading, setLoading] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [taxNumber, setTaxNumber] = useState("");
  const [giroNumber, setGiroNumber] = useState("");
  const [contact, setContact] = useState("");
  const [position, setPosition] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");

  const [companyNameErr, setCompanyNameErr] = useState("");
  const [taxNumberErr, setTaxNumberErr] = useState("");
  const [giroNumberErr, setGiroNumberErr] = useState("");
  const [contactErr, setContactErr] = useState("");
  const [positionErr, setPositionErr] = useState("");
  const [mobileErr, setMobileErr] = useState("");
  const [emailErr, setEmailErr] = useState("");
  const [snackbarState, setSnackbarState] = useState(snackbarInitialState);

  const validateFields = () => {
    if (!useValidator) {
      return false;
    }
    let hasError = false;
    if (companyName.length === 0) {
      hasError = true;
      setCompanyNameErr(langContext.dictionary.cannotBeEmpty);
    } else {
      setCompanyNameErr("");
    }
    if (taxNumber.length === 0) {
      hasError = true;
      setTaxNumberErr(langContext.dictionary.cannotBeEmpty);
    } else {
      if (/^(\d{7})(\d)-([1-5])-(0[2-9]|[13][0-9]|2[02-9]|4[0-4]|51)$/.test(taxNumber)) {
        setTaxNumberErr("");
      } else {
        hasError = true;
        setTaxNumberErr(langContext.dictionary.taxFormatError);
      }
    }
    if (giroNumber.length === 0) {
      hasError = true;
      setGiroNumberErr(langContext.dictionary.cannotBeEmpty);
    } else {
      setGiroNumberErr("");
    }
    if (contact.length === 0) {
      hasError = true;
      setContactErr(langContext.dictionary.cannotBeEmpty);
    } else {
      setContactErr("");
    }
    if (position.length === 0) {
      hasError = true;
      setPositionErr(langContext.dictionary.cannotBeEmpty);
    } else {
      setPositionErr("");
    }
    if (mobile.length === 0) {
      hasError = true;
      setMobileErr(langContext.dictionary.cannotBeEmpty);
    } else {
      if (/^\+?[1-9][0-9]{7,14}$/.test(mobile)) {
        setMobileErr("");
      } else {
        hasError = true;
        setMobileErr(langContext.dictionary.mobileFormatError);
      }
    }
    if (email.length === 0) {
      hasError = true;
      setEmailErr(langContext.dictionary.cannotBeEmpty);
    } else {
      if (/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(email)) {
        setEmailErr("");
      } else {
        hasError = true;
        setEmailErr(langContext.dictionary.formatError);
      }
    }

    return hasError;
  };

  const clear = () => {
    setCompanyName("");
    setCompanyNameErr("");
    setTaxNumber("");
    setTaxNumberErr("");
    setGiroNumber("");
    setGiroNumberErr("");
    setContact("");
    setContactErr("");
    setPosition("");
    setPositionErr("");
    setMobile("");
    setMobileErr("");
    setEmail("");
    setEmailErr("");
  };

  const submit = () => {
    if (validateFields()) {
      return;
    }

    setLoading(true);
    ReactRecaptcha3
      .getToken()
      .then((resp) => {
          let requestData = {
            token: resp,
            companyName: companyName,
            taxNumber: taxNumber,
            giroNumber: giroNumber,
            contact: contact,
            position: position,
            mobile: mobile,
            email: email
          };
          axios.post(`${env.apiRoot}/registration`, requestData).then(response => {
            clear();
            setSnackbarState({open: true, message: langContext.dictionary.success, severity: "success"});
          })
            .catch(error => {
              console.error(error);
              setSnackbarState({
                open: true,
                message: langContext.dictionary.errorDuringRegistration + " " + error.message,
                severity: "error"
              });
            })
            .finally(() => {
              setLoading(false);
            });
        }
      )
      .catch((error) => {
        console.error(error);
        setSnackbarState({
          open: true,
          message: langContext.dictionary.errorDuringRegistration,
          severity: "error"
        });
        setLoading(false);
      });
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarState(snackbarInitialState);
  };

  return (
    <>
      <Card>
        <CardContent>
          <>
            <Typography gutterBottom variant="h5" component="div">
              {langContext.dictionary.registration}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {langContext.dictionary.regDescr}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{paddingTop: 3}}>
              *{langContext.dictionary.mandatory}
            </Typography>
            <Grid container spacing={2} sx={{paddingTop: 3}}>
              <Grid item xs={12} md={12}>
                <TextField
                  id="outlined-basic" fullWidth label={langContext.dictionary.fields.companyName}
                  variant={"outlined"}
                  onChange={evt => setCompanyName(evt.target.value)}
                  error={companyNameErr.length > 0}
                  helperText={companyNameErr}
                  value={companyName}
                  required
                  inputProps={{maxLength: 255}}
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <TextField
                  id="outlined-basic" fullWidth label={langContext.dictionary.fields.taxNumber}
                  variant={"outlined"}
                  error={taxNumberErr.length > 0}
                  helperText={taxNumberErr}
                  placeholder={"12345678-1-12"}
                  onChange={evt => setTaxNumber(evt.target.value)}
                  value={taxNumber}
                  required
                  inputProps={{maxLength: 255}}
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <TextField
                  id="outlined-basic" fullWidth label={langContext.dictionary.fields.giroNumber}
                  variant={"outlined"}
                  error={giroNumberErr.length > 0}
                  helperText={giroNumberErr}
                  onChange={evt => setGiroNumber(evt.target.value)}
                  value={giroNumber}
                  required
                  inputProps={{maxLength: 255}}
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <TextField
                  id="outlined-basic" fullWidth label={langContext.dictionary.fields.contact}
                  variant={"outlined"}
                  error={contactErr.length > 0}
                  helperText={contactErr}
                  onChange={evt => setContact(evt.target.value)}
                  value={contact}
                  required
                  inputProps={{maxLength: 255}}
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <TextField
                  id="outlined-basic" fullWidth label={langContext.dictionary.fields.position}
                  variant={"outlined"}
                  error={positionErr.length > 0}
                  helperText={positionErr}
                  onChange={evt => setPosition(evt.target.value)}
                  value={position}
                  required
                  inputProps={{maxLength: 255}}
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <TextField
                  id="outlined-basic" fullWidth label={langContext.dictionary.fields.mobile}
                  variant={"outlined"}
                  error={mobileErr.length > 0}
                  helperText={mobileErr}
                  placeholder={"+36..."}
                  onChange={evt => setMobile(evt.target.value)}
                  value={mobile}
                  required
                  inputProps={{maxLength: 255}}
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <TextField
                  id="outlined-basic" fullWidth label={langContext.dictionary.fields.email}
                  variant={"outlined"}
                  error={emailErr.length > 0}
                  helperText={emailErr}
                  onChange={evt => setEmail(evt.target.value)}
                  value={email}
                  inputMode={"email"}
                  required
                  inputProps={{maxLength: 255}}
                />
              </Grid>
            </Grid>
          </>
        </CardContent>
        <CardActions>
          <LoadingButton
            loading={loading}
            variant="contained"
            color="mainButton"
            onClick={submit}
            sx={{marginLeft: 1, marginBottom: 1}}
          >
            {langContext.dictionary.registration}
          </LoadingButton>
        </CardActions>
      </Card>
      <Snackbar open={snackbarState.open} autoHideDuration={6000} onClose={handleSnackbarClose} anchorOrigin={{vertical: 'top', horizontal: 'center'}}>
        <Alert onClose={handleSnackbarClose} severity={snackbarState.severity} sx={{width: '100%'}} variant="filled">
          {snackbarState.message}
        </Alert>
      </Snackbar>
    </>
  );

};

export default Registration;
